import { BASE_CELL_COUNT, GRID_SIZE, activeCells, cellIndex, rowOf } from "../gridSize";
import { lineRows } from "../rules/lines";
import {
  BLANK,
  COLLECT,
  EMPTY_POTS,
  JACKPOT_COIN_IDS,
  MYSTERY,
  MYSTERY_JACKPOT,
  POTS,
  POT_CAPACITY,
  coinId,
  collectId,
  isBonusSymbolId,
  isMysteryId,
  parseSymbolId,
  type PotKind,
  type PotLevels,
} from "../rules/symbols";
import { blankBoard, boardValueMinor, cellsOfRows, collectSources, coveredLines, finalPay, heldCells, isFull } from "./holdRules";
import {
  BASE_SYMBOL_WEIGHTS,
  BONUS_BUYS,
  COIN_VALUE_TENTHS,
  MADNESS_DROPS_PER_RESPIN,
  MARK_CHANCE,
  MULTI_DROPS,
  MYSTERY_JACKPOT_REVEAL_WEIGHTS,
  MYSTERY_REVEAL_WEIGHTS,
  POT_TRIGGER_CHANCE,
  RESPINS,
  RESPIN_LAND_CHANCE,
  RESPIN_SYMBOL_WEIGHTS,
  type BonusBuyId,
} from "./offlineMath";
import { pickWeighted, randomInt, shuffled, type Rng } from "./rng";
import type { BonusSession, Boosters, RespinStep, RoundOutcome, SpinOutcome } from "./roundTypes";

/**
 * The offline server's game: what a backend would play for a spin request, as plain records (`roundTypes.ts`) the
 * round builder turns into the wire's events. The rules (what is held, collected, reset, refilled, worth and paid) are
 * applied here once; what is drawn — the boards, the gems, the landings, the eruptions, the reveals — comes from a
 * source of decisions: random for the live offline server, a script for the dev and e2e rounds.
 */

export interface OfflineServerState {
  pots: PotLevels;
  /** The line's bet the last round was played at: a round at another starts with empty pots; another line count keeps them. */
  lastLineBetMinor: string | null;
}

/** What the bonus draws: the rules ask, a random source or a script answers. */
export interface BonusDecisions {
  /** What lands in respin `respin` (1-based), among `emptyCells`: cell → symbol id (`COL` for a collect). */
  land(respin: number, emptyCells: readonly number[]): ReadonlyMap<number, string>;
  /** The cells MULTI hits in respin `respin` (a cell may repeat); `mayBeLast`: nothing more will land unless LIFE refills. */
  erupt(respin: number, mayBeLast: boolean, cellsInPlay: readonly number[]): readonly number[];
  /** What the mystery at `index` reveals: a coin or a jackpot coin, a jackpot coin only for a mystery jackpot (`jackpot`). */
  reveal(index: number, jackpot: boolean): string;
}

/** A coin worth `tenths` tenths of the bet (at least one minor unit). */
export function coinWorth(betMinor: bigint, tenths: number): string {
  const value = (betMinor * BigInt(tenths)) / 10n;
  return coinId(value > 0n ? value : 1n);
}

/**
 * Settles a landed base board: the gems into the pots (a gem counts only in an active line's row; a full pot still takes
 * one's comet, its level staying full), the pots after, and the trigger — a covered active line, a full pot at the server's draw (`potTrigger`:
 * a pot stays full, spin after spin, until the draw starts its bonus), or the buy. The bonus's boosters are the pots
 * charged after the spin, a buy's included.
 */
export function settleSpin(input: {
  board: readonly string[];
  potsBefore: PotLevels;
  marks: readonly { index: number; pot: PotKind }[];
  /** A bought bonus: its boosters (their pots light up; a bought board carries no gems). */
  buy: Boosters | null;
  /** The active lines' rows (decision 56). */
  rows: readonly number[];
  /** The server's draw: a full pot starts the bonus on this spin. */
  potTrigger: boolean;
}): SpinOutcome {
  const { board, potsBefore, buy, rows } = input;
  const levels: Record<PotKind, number> = { ...potsBefore };
  const marks: { index: number; pot: PotKind }[] = [];
  for (const mark of input.marks) {
    if (!rows.includes(rowOf(mark.index))) continue;
    levels[mark.pot] = Math.min(POT_CAPACITY, levels[mark.pot] + 1);
    marks.push(mark);
  }
  if (buy) for (const pot of POTS) if (buy[pot]) levels[pot] = Math.max(levels[pot], 1);
  const lines = coveredLines(board, rows);
  const fullPots = POTS.filter((pot) => levels[pot] >= POT_CAPACITY);
  const reason = buy ? "buy" : lines.length > 0 ? "line" : fullPots.length > 0 && input.potTrigger ? "pot" : null;
  const boosters: Boosters = { life: levels.life > 0, multi: levels.multi > 0, grow: levels.grow > 0, multiEveryRespin: buy?.multiEveryRespin ?? false };
  const cells = reason === "pot" ? marks.filter((m) => fullPots.includes(m.pot)).map((m) => m.index) : cellsOfRows(lines);
  return {
    board,
    marks,
    potsBefore,
    potsAfter: levels,
    anticipatedCells: buy ? [] : anticipatedCells(board, rows),
    trigger: reason ? { reason, cells, boosters } : null,
  };
}

/** An active line one symbol short as its last cell starts to stop: that cell teases (the cells land row by row). */
function anticipatedCells(board: readonly string[], rows: readonly number[]): number[] {
  const last = GRID_SIZE.columns - 1;
  const teased: number[] = [];
  for (const row of rows) {
    let lead = true;
    for (let col = 0; col < last; col++) if (!isBonusSymbolId(board[cellIndex(row, col)]!)) lead = false;
    if (lead) teased.push(cellIndex(row, last));
  }
  return teased;
}

/**
 * The hold-and-win session from the trigger board: respins until the meter runs out (LIFE refilling it once) or every
 * cell in play is filled. Each respin lands what the decisions draw in the empty cells, the meter resetting when
 * anything lands; a collect collects the coins and jackpot coins in view (in reading order); MULTI's hits raise their
 * cells' multipliers (×2, then +1). Then the mysteries are revealed and the board pays: its values at `betMinor` (what
 * they are multiples of), the cap at `stakeMinor` (what the round cost).
 */
export function runBonus(betMinor: bigint, stakeMinor: bigint, startBoard: readonly string[], boosters: Boosters, decisions: BonusDecisions): BonusSession {
  const grown = boosters.grow;
  const board = [...startBoard];
  const multipliers = new Map<number, number>();
  const cellsInPlay = activeCells(grown);
  let count = RESPINS;
  let lifeLeft = boosters.life;
  const respins: RespinStep[] = [];

  while (!isFull(board, grown)) {
    let refilled = false;
    if (count === 0) {
      if (!lifeLeft) break;
      lifeLeft = false;
      count = RESPINS;
      refilled = true;
    }
    count -= 1;
    const respin = respins.length + 1;
    const countAtStart = count;
    const held = heldCells(board, grown);
    const empty = cellsInPlay.filter((index) => !isBonusSymbolId(board[index]!));
    const landing = decisions.land(respin, empty);
    const landed: number[] = [];
    for (const index of empty) {
      const id = landing.get(index);
      if (id === undefined) continue;
      if (!isBonusSymbolId(id) || (id !== COLLECT && parseSymbolId(id) === null)) throw new Error(`runBonus: respin ${respin} lands "${id}" in cell ${index}`);
      board[index] = id;
      landed.push(index);
    }
    for (const index of landing.keys()) if (!empty.includes(index)) throw new Error(`runBonus: respin ${respin} lands in cell ${index}, which is not empty and in play`);
    const landedBoard = [...board];

    const collects: RespinStep["collects"][number][] = [];
    for (const index of landed) {
      if (board[index] !== COLLECT) continue;
      const sources = collectSources(board, index, grown, betMinor);
      const value = sources.reduce((sum, source) => sum + BigInt(source.amountMinor), 0n);
      board[index] = collectId(value);
      collects.push({ collectorIndex: index, sources, valueMinor: value.toString() });
    }

    const mayBeLast = (landed.length === 0 && count === 0 && !lifeLeft) || isFull(board, grown);
    const drops: { index: number; multiplier: number }[] = [];
    for (const index of decisions.erupt(respin, mayBeLast, cellsInPlay)) {
      if (!cellsInPlay.includes(index)) throw new Error(`runBonus: respin ${respin} drops a multiplier on cell ${index}, which is not in play`);
      const multiplier = (multipliers.get(index) ?? 1) === 1 ? 2 : multipliers.get(index)! + 1;
      multipliers.set(index, multiplier);
      drops.push({ index, multiplier });
    }

    if (landed.length > 0) count = RESPINS;
    respins.push({
      refilled,
      countAtStart,
      held,
      landedBoard,
      landed,
      collects,
      drops,
      board: [...board],
      countAfter: count,
      boardValueMinor: boardValueMinor(board, multipliers, grown, betMinor).toString(),
    });
  }

  const mysteries: { index: number; symbolId: string }[] = [];
  for (const index of cellsInPlay) {
    if (!isMysteryId(board[index]!)) continue;
    const jackpot = board[index] === MYSTERY_JACKPOT;
    const id = decisions.reveal(index, jackpot);
    const symbol = parseSymbolId(id);
    const allowed = symbol?.kind === "jackpot" || (!jackpot && symbol?.kind === "coin");
    if (!allowed) throw new Error(`runBonus: ${jackpot ? "mystery jackpot" : "mystery"} ${index} reveals "${id}"`);
    board[index] = id;
    mysteries.push({ index, symbolId: id });
  }
  const pay = finalPay(board, multipliers, grown, betMinor, stakeMinor);
  return {
    boosters,
    startBoard,
    startValueMinor: boardValueMinor(startBoard, new Map(), grown, betMinor).toString(),
    respins,
    end: { mysteries, fullBoard: pay.fullBoard, cells: pay.cells, totalMinor: pay.totalMinor },
  };
}

// ── The random server ───────────────────────────────────────────────────────────────────────────────────────────

function randomCoin(rng: Rng, betMinor: bigint): string {
  return coinWorth(betMinor, pickWeighted(rng, COIN_VALUE_TENTHS));
}

function baseSymbol(rng: Rng, betMinor: bigint): string {
  const kind = pickWeighted(rng, BASE_SYMBOL_WEIGHTS);
  if (kind === "blank") return BLANK;
  if (kind === "coin") return randomCoin(rng, betMinor);
  if (kind === "mystery") return MYSTERY;
  if (kind === "mysteryJackpot") return MYSTERY_JACKPOT;
  return JACKPOT_COIN_IDS[kind];
}

function randomBaseBoard(rng: Rng, betMinor: bigint): string[] {
  const board = blankBoard();
  for (let index = 0; index < BASE_CELL_COUNT; index++) board[index] = baseSymbol(rng, betMinor);
  return board;
}

/** Each landed coin in an active line's row may carry a gem into a random pot. */
function randomMarks(rng: Rng, board: readonly string[], rows: readonly number[]): { index: number; pot: PotKind }[] {
  const marks: { index: number; pot: PotKind }[] = [];
  for (let index = 0; index < BASE_CELL_COUNT; index++) {
    if (!rows.includes(rowOf(index))) continue;
    if (parseSymbolId(board[index]!)?.kind === "coin" && rng() < MARK_CHANCE) marks.push({ index, pot: POTS[randomInt(rng, 0, POTS.length - 1)]! });
  }
  return marks;
}

/**
 * A base spin: every base cell drawn on its own, then the gems (`rows`: the active lines'), then the draw that starts a
 * full pot's bonus.
 */
export function playSpin(rng: Rng, betMinor: bigint, potsBefore: PotLevels, rows: readonly number[]): SpinOutcome {
  const board = randomBaseBoard(rng, betMinor);
  const marks = randomMarks(rng, board, rows);
  return settleSpin({ board, potsBefore, marks, buy: null, rows, potTrigger: rng() < POT_TRIGGER_CHANCE });
}

/**
 * A bought spin: one active line covered for sure, the rest drawn as usual; the buy's boosters, with any pot already
 * charged.
 */
export function playBuy(rng: Rng, betMinor: bigint, potsBefore: PotLevels, buyId: BonusBuyId, rows: readonly number[]): SpinOutcome {
  const board = randomBaseBoard(rng, betMinor);
  const row = rows[randomInt(rng, 0, rows.length - 1)]!;
  for (let col = 0; col < GRID_SIZE.columns; col++) {
    const index = cellIndex(row, col);
    if (!isBonusSymbolId(board[index]!)) board[index] = randomCoin(rng, betMinor);
  }
  const kind = BONUS_BUYS[buyId].boosters;
  const bought = kind === "random" ? new Set(shuffled(rng, POTS).slice(0, randomInt(rng, 1, POTS.length))) : new Set(POTS);
  return settleSpin({ board, potsBefore, marks: [], buy: { life: bought.has("life"), multi: bought.has("multi"), grow: bought.has("grow"), multiEveryRespin: kind === "madness" }, rows, potTrigger: false });
}

/**
 * The random bonus: each empty cell lands with the board's chance; MULTI erupts at random while it has drops left — at
 * least once, on what may be the last respin at the latest — or on every respin with the Madness buy.
 */
export function randomDecisions(rng: Rng, betMinor: bigint, boosters: Boosters): BonusDecisions {
  const landChance = boosters.grow ? RESPIN_LAND_CHANCE.grown : RESPIN_LAND_CHANCE.base;
  let dropsLeft = boosters.multi ? randomInt(rng, MULTI_DROPS.min, MULTI_DROPS.max) : 0;
  let erupted = false;
  return {
    land: (_respin, emptyCells) => {
      const landing = new Map<number, string>();
      for (const index of emptyCells) {
        if (rng() >= landChance) continue;
        const kind = pickWeighted(rng, RESPIN_SYMBOL_WEIGHTS);
        landing.set(index, kind === "coin" ? randomCoin(rng, betMinor) : kind === "collect" ? COLLECT : kind === "mystery" ? MYSTERY : kind === "mysteryJackpot" ? MYSTERY_JACKPOT : JACKPOT_COIN_IDS[kind]);
      }
      return landing;
    },
    erupt: (_respin, mayBeLast, cellsInPlay) => {
      let n = 0;
      if (boosters.multiEveryRespin) {
        n = randomInt(rng, MADNESS_DROPS_PER_RESPIN.min, MADNESS_DROPS_PER_RESPIN.max);
      } else if (dropsLeft > 0 && (rng() < MULTI_DROPS.eruptChance || (!erupted && mayBeLast))) {
        n = Math.min(dropsLeft, randomInt(rng, MULTI_DROPS.perEruption.min, MULTI_DROPS.perEruption.max));
        dropsLeft -= n;
      }
      if (n > 0) erupted = true;
      return Array.from({ length: n }, () => cellsInPlay[randomInt(rng, 0, cellsInPlay.length - 1)]!);
    },
    reveal: (_index, jackpot) => {
      if (jackpot) return JACKPOT_COIN_IDS[pickWeighted(rng, MYSTERY_JACKPOT_REVEAL_WEIGHTS)];
      const kind = pickWeighted(rng, MYSTERY_REVEAL_WEIGHTS);
      return kind === "coin" ? randomCoin(rng, betMinor) : JACKPOT_COIN_IDS[kind];
    },
  };
}

/** A request's bet (decision 56): what it costs, the lines it covers — its line's bet, and the bet its values follow. */
export function betOf(betMinor: string, lines: number, bought: boolean): { stake: bigint; lineBet: bigint; valueBet: bigint; rows: readonly number[] } {
  const stake = BigInt(betMinor);
  const lineBet = stake / BigInt(lines);
  if (lineBet * BigInt(lines) !== stake) throw new Error(`betOf: a bet of ${betMinor} is not ${lines} whole lines`);
  // A triggered bonus's values follow a line's bet; a bought bonus's, the whole bet it was priced on (the user's choice).
  return { stake, lineBet, valueBet: bought ? stake : lineBet, rows: lineRows(lines) };
}

/**
 * One random round for a spin request: the pots at its line's bet (another starts empty; another line count keeps
 * them), the spin or the buy, its bonus.
 */
export function playRound(rng: Rng, state: OfflineServerState, betMinor: string, lines: number, buyId: BonusBuyId | null): RoundOutcome {
  const bet = betOf(betMinor, lines, buyId !== null);
  const lineBetMinor = bet.lineBet.toString();
  if (state.lastLineBetMinor !== lineBetMinor) state.pots = EMPTY_POTS;
  state.lastLineBetMinor = lineBetMinor;
  const spin = buyId ? playBuy(rng, bet.valueBet, state.pots, buyId, bet.rows) : playSpin(rng, bet.valueBet, state.pots, bet.rows);
  const bonus = spin.trigger ? runBonus(bet.valueBet, bet.stake, spin.board, spin.trigger.boosters, randomDecisions(rng, bet.valueBet, spin.trigger.boosters)) : null;
  // The bonus spends every pot; otherwise the pots keep what the spin left.
  state.pots = bonus ? EMPTY_POTS : spin.potsAfter;
  return { betMinor, lines, lineBetMinor, valueBetMinor: bet.valueBet.toString(), spin, bonus, winMinor: bonus?.end.totalMinor ?? "0" };
}
